import React, {useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {AuxiliaryToolTip, Icon} from "@spg/component_library";
import {TopicDescriptionInfo} from "../../../logic/APITypes";
import Select from "react-select";
import infoIcon from "../../../images/info.svg";
import {generateTopicDescriptionText} from "../../../logic/IntlTexts";
import {doesListSatisfyFilter} from "../../../logic/ListFilter";
import {useNavigate} from "react-router-dom";

/**
 * A widget to search for a topic to inspect.
 */
function TopicContentViewerSearch(props: {
  topicList: TopicDescriptionInfo[];
  currentId: string;
  disabled: boolean;
  closeMenu: () => void;
  setLoadingTopic: (name: string) => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();

  const [input, setInput] = useState("");

  const topicFilterList: Record<string, string[]> = {};
  props.topicList.forEach(
    (topic) =>
      (topicFilterList[topic.id] = [
        topic.name,
        topic.owner_name,
        topic.id,
        generateTopicDescriptionText(intl, topic, "METHOD"),
        generateTopicDescriptionText(intl, topic, "HEALTH"),
        topic.description,
      ]),
  );

  const toolTipForTopic = (topic: TopicDescriptionInfo) => {
    const lines = [
      ["owner", topic.owner_name],
      [
        "status",
        intl.formatMessage({
          id: "topicDescriptionConnector.health." + topic.health,
        }),
      ],
      [
        "transferType",
        intl.formatMessage({
          id:
            "topicDescriptionConnector.method." +
            (topic.transfer_type === "REST"
              ? "rest_" + topic.direction.toLowerCase()
              : topic.transfer_type.toLowerCase()),
        }),
      ],
      [
        "visibility",
        intl.formatMessage({
          id:
            "topicDescriptionCreation.inputLabel.visibility." +
            topic.visibility.toLowerCase(),
        }),
      ],
    ].map(
      (line) =>
        `${intl.formatMessage({
          id: "topicContentViewer.infoBlock.key." + line[0],
        })}: ${line[1]}`,
    );
    lines.unshift(
      `${intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.description",
      })}: ${(topic.description.length > 30 ? topic.description.substring(0, 29) + "…" : topic.description).replaceAll("\n", " ")}`,
    );
    return lines.join("\n");
  };

  return (
    <Select
      unstyled
      className={
        "topicContentViewerSearchSelect" +
        (props.topicList.filter((topic) =>
          doesListSatisfyFilter(topicFilterList[topic.id], input),
        ).length > 6
          ? " overflowing"
          : "")
      }
      classNamePrefix="topicContentViewerSearchSelect"
      components={{
        DropdownIndicator: undefined,
      }}
      isDisabled={props.disabled}
      options={props.topicList.map((topic, i) => ({
        value: topic.id,
        label: (
          <div
            className={"topicContentViewerSearchEntry"}
            aria-description={toolTipForTopic(topic)}
          >
            <AuxiliaryToolTip
              toolTip={toolTipForTopic(topic)}
              idBase={"topicContentViewerSearchEntry" + topic.id}
              tooltipPosition={
                i < props.topicList.length / 2 ? "bottom" : "top"
              }
              toggleMessage={intl.formatMessage({
                id: "topicContentViewer.search.info",
              })}
            >
              <div className={"topicContentViewerSearchEntryText"}>
                {topic.name}
              </div>
              <Icon
                href={infoIcon + "#globalG"}
                title={""}
                strokeColor="black"
              />
            </AuxiliaryToolTip>
          </div>
        ),
      }))}
      onInputChange={(value) => setInput(value)}
      isClearable={false}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      menuIsOpen
      onChange={(value) => {
        if (value && value.value != props.currentId) {
          navigate("/topics/datasets/" + value.value);
          props.setLoadingTopic(
            props.topicList.find((topic) => topic.id === value.value)?.name ||
              "",
          );
        }
        props.closeMenu();
      }}
      onBlur={props.closeMenu}
      value={{
        value: props.currentId,
        label: (
          <div className="topicContentViewerSearch">
            {
              props.topicList.find((topic) => topic.id === props.currentId)
                ?.name
            }
          </div>
        ),
      }}
      filterOption={(option, input) =>
        doesListSatisfyFilter(topicFilterList[option.value], input)
      }
      noOptionsMessage={() => (
        <span className="topicContentViewerSearchEmpty">
          <FormattedMessage id="topicContentViewer.search.empty" />
        </span>
      )}
    />
  );
}

export {TopicContentViewerSearch};
